import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { PublicFrame } from '../../components/Shell'
import { Turnstile } from '../../components/Turnstile'
import { ErrorNote, Field } from '../../components/ui'
import { api } from '../../lib/api'
import { useT } from '../../lib/i18n'
import { sendMagicLink } from '../../lib/session'

export function LoginPage() {
  const { t } = useT()
  const params = new URLSearchParams(location.search)
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [captcha, setCaptcha] = useState<string | null>(null)
  const [captchaReset, setCaptchaReset] = useState(0)
  const [error, setError] = useState<unknown>(params.get('error') ? { code: params.get('error') } : null)
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: () => api<{ google: boolean; github: boolean; turnstileSiteKey: string | null }>('/api/config'),
  })
  const next = params.get('next') ?? '/'

  const social = async (provider: 'google' | 'github') => {
    const r = await api<{ url: string }>('/api/auth/sign-in/social', {
      body: { provider, callbackURL: next },
    })
    location.href = r.url
  }

  return (
    <PublicFrame>
      <div className="glow mx-auto grid min-h-[calc(100vh-64px)] max-w-[1200px] items-center gap-12 px-4 py-16 md:grid-cols-2 md:px-12">
        <div>
          <div className="eyebrow-ink mb-6">Referral &amp; Affiliate Solution</div>
          <h1 className="display mb-6 text-[44px] md:text-[64px]">{t('loginTitle')}</h1>
          <p className="max-w-md text-lg text-body">{t('loginLead')}</p>
        </div>
        <div className="card card-pad md:p-10">
          {sent ? (
            <div aria-live="polite">
              <div className="eyebrow mb-3">/ 0.2</div>
              <h2 className="h2 mb-3">{t('checkInbox')}</h2>
              <p className="text-body">{t('checkInboxText', { email })}</p>
              <button type="button" className="btn mt-8" onClick={() => setSent(false)}>
                ← {t('email')}
              </button>
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setBusy(true)
                setError(null)
                try {
                  await sendMagicLink(email, next, captcha)
                  setSent(true)
                } catch (err) {
                  setError(err)
                  setCaptchaReset((n) => n + 1)
                } finally {
                  setBusy(false)
                }
              }}
            >
              <div className="eyebrow mb-3">/ 0.1</div>
              <h2 className="h2 mb-8">{t('signIn')}</h2>
              <Field label={t('email')}>
                <input
                  className="input"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
              </Field>
              <Turnstile siteKey={cfg.data?.turnstileSiteKey} onToken={setCaptcha} resetKey={captchaReset} />
              <button
                type="submit"
                className="btn btn-primary mt-6 w-full"
                disabled={busy || (!!cfg.data?.turnstileSiteKey && !captcha)}
              >
                {t('continueEmail')}
              </button>
              <ErrorNote error={error} />
              {(cfg.data?.google || cfg.data?.github) && (
                <>
                  <div className="eyebrow my-6 text-center">{t('orContinue')}</div>
                  <div className="grid grid-cols-2 gap-3">
                    {cfg.data.google && (
                      <button type="button" className="btn" onClick={() => social('google')}>
                        Google
                      </button>
                    )}
                    {cfg.data.github && (
                      <button type="button" className="btn" onClick={() => social('github')}>
                        GitHub
                      </button>
                    )}
                  </div>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </PublicFrame>
  )
}
